import { useCallback, useState } from "react";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from '@core/types/errorResponse';
import { fetchDepartmentByIdAsync, updateDepartmentAsync, enableDepartmentAsync, disableDepartmentAsync } from "@features/department/services/departmentSvc";
import { Department } from "@features/department/types/department";
import { UpdateDepartmentRequest } from "../types/departmentRequest";

export function useDepartmentActions() {
    const [busy, setBusy] = useState(false);
    const [department, setDepartment] = useState<Department[]>([]);
    const showError = useApiError();

    /**
     * Fetches a department by its ID, updates local state, and returns the fetched payload.
     *
     * Uses fetchDepartmentByIdAsync to retrieve department data. If the response indicates success,
     * the hook updates component state by calling setDepartment(...) with the returned data (or an empty
     * array if no data is present) and resolves with the raw response payload. If the response indicates
     * failure, it calls showError(...) with the error information and resolves with null.
     *
     * If an exception is thrown during the fetch, showError(...) is invoked with the thrown error and
     * the error is rethrown so callers can handle it.
     *
     * @param id - The identifier of the department to fetch.
     * @returns Promise resolving to the response data when successful, or null when the response indicates failure.
     * @throws Rethrows any exception encountered during the fetch after reporting it via showError.
     */
    const fetchDepartment = useCallback(async (
        id: number,
        scope?: { churchId?: number; districtId?: number; nationId?: number }
    ) => {
        try {
            // Fetch department by ID
            const response = await fetchDepartmentByIdAsync(id, scope);
            if (response.isSuccess()) {
                // Update local state with fetched department data
                setDepartment(response.getData()?.data ?? []);
                return response.getData();
            } else {
                // Show error if fetch failed
                showError(response.getError() as ErrorResponse);
                return null;
            }
        } catch (error) {
            // Show error for any thrown exceptions
            showError(error as ErrorResponse);
            throw error;
        }
    }, [showError]);

    /**
     * Asynchronously updates a department for a given nation and department id.
     *
     * Sets the busy indicator to true, invokes updateDepartmentAsync with the provided
     * nationId, id, and data, then sets the busy indicator to false and returns the API response.
     *
     * @param nationId - The identifier of the nation the department belongs to.
     * @param id - The identifier of the department to update.
     * @param data - The payload containing the department fields to update.
     * @returns A promise that resolves with the response returned by updateDepartmentAsync.
     *
     * @remarks
     * - Side effects: toggles the busy state via setBusy(true) before the request and setBusy(false)

     *   after the request completes successfully.
     * - Errors thrown by updateDepartmentAsync will propagate to the caller; in that case, the busy
     *   state may not be reset by this function. Consider wrapping the call in a try/finally block
     *   if you need to guarantee that the busy state is cleared on failure.
     */
    const doDepartmentUpdate = useCallback(async (nationId: number, id: number, data: UpdateDepartmentRequest) => {
        setBusy(true);
        const response = await updateDepartmentAsync(nationId, id, data);
        setBusy(false);
        return response;
    }, []);

    /** Disables a department (sets it inactive). */
    const doDepartmentDisable = useCallback(async (nationId: number, id: number) => {
        setBusy(true);
        const response = await disableDepartmentAsync(nationId, id);
        setBusy(false);
        return response;
    }, []);

    /** Enables a department (sets it active). */
    const doDepartmentEnable = useCallback(async (nationId: number, id: number) => {
        setBusy(true);
        const response = await enableDepartmentAsync(nationId, id);
        setBusy(false);
        return response;
    }, []);

    return { busy, department, fetchDepartment, doDepartmentUpdate, doDepartmentDisable, doDepartmentEnable };
}